import { groupBy, uniqBy } from 'lodash';
import { createSearchPredictionModel } from 'data/search-predictions';
import type SearchPrediction from 'data/search-predictions';
import { SearchPredictionSource } from 'utils/google-maps/types';
import { isPostalCodeLabel, isProvinceLabel } from 'utils/search-predictions';

/**
 * Deduplicates predictions by description and preserves location IDs from Google
 */
export const uniqByDescriptionAndAddLocationId = (predictions: SearchPrediction[]): SearchPrediction[] => {
  const resultMap: {[description: string]: SearchPrediction } = {};
  for (const prediction of predictions) {
    const descriptionKey = prediction.description.toLowerCase();
    if (descriptionKey in resultMap) {
      const existingLocation = resultMap[descriptionKey];
      if (prediction.source === SearchPredictionSource.GOOGLE) {
        existingLocation.locationId = prediction.id;
        existingLocation.source = prediction.source;
      }
    } else {
      resultMap[descriptionKey] = { ...prediction } as SearchPrediction;
    }
  }
  return Object.values(resultMap);
};

/**
 * Models city predictions consistently
 */
export const modelingCities = (cities: SearchPrediction[]): SearchPrediction[] =>
  cities.map(createSearchPredictionModel);

/**
 * Processes and combines predictions from go-search and Google
 */
export const processPredictions = (
  goSearchPredictions: SearchPrediction[],
  googleSearchPredictions: SearchPrediction[],
  isHomeAppraisalSearch: boolean = false
): SearchPrediction[] => {
  const predictionsGroupedByGroup = groupBy([...goSearchPredictions, ...googleSearchPredictions], 'group');
  const { listings = [], schools = [], buildings = [], locations = [], militaryBases = []} = predictionsGroupedByGroup;

  // Group locations by label, but treat province and state as the same category
  const locationsGroupedByLabel = groupBy(locations, location => {
    if (isProvinceLabel(location.label as string)) return 'province';
    return location.label;
  });
  const { city = [], neighbourhood = [], street = [], province = []} = locationsGroupedByLabel;

  // Remove duplicate cities that could come from the go-search and preserve locationId from Google
  const citiesWithLocationIds = uniqByDescriptionAndAddLocationId(city);
  const uniqCities = modelingCities(citiesWithLocationIds);

  // Remove duplicates that could come from the SearchPredictionStore
  const UNIQUENESS_ATTRIBUTE = 'description';
  const uniqNeighborhoods = uniqBy(neighbourhood, UNIQUENESS_ATTRIBUTE);
  const uniqStreets = uniqBy(street, UNIQUENESS_ATTRIBUTE);
  const uniqProvinces = uniqBy(province, UNIQUENESS_ATTRIBUTE);

  let finalPredictions: SearchPrediction[] = [];

  if (isHomeAppraisalSearch) {
    finalPredictions = [
      ...listings,
      ...uniqCities,
      ...uniqStreets,
      ...uniqProvinces,
    ];
  } else {
    finalPredictions = [
      ...militaryBases,
      ...listings,
      ...schools,
      ...buildings,
      ...uniqCities,
      ...uniqNeighborhoods,
      ...uniqStreets,
      ...uniqProvinces,
    ];
  }

  return finalPredictions;
};

/**
 * Processes and combines predictions from go-search and Google for commercial search
 */
export const processPredictionsForCommercial = (
  goSearchPredictions: SearchPrediction[],
  googleSearchPredictions: SearchPrediction[]
): SearchPrediction[] => {
  const predictionsGroupedByGroup = groupBy([...goSearchPredictions, ...googleSearchPredictions], 'group');
  const { listings = [], locations = []} = predictionsGroupedByGroup;

  // Group locations by label, but treat province and state as the same category
  const locationsGroupedByLabel = groupBy(locations, location => {
    if (isProvinceLabel(location.label as string)) return 'province';
    if (isPostalCodeLabel(location.label as string)) return 'postalCode';
    return location.label;
  });
  const { city = [], province = [], postalCode = []} = locationsGroupedByLabel;

  // Remove duplicate cities that could come from the go-search and preserve locationId from Google
  const citiesWithLocationIds = uniqByDescriptionAndAddLocationId(city);
  const uniqCities = modelingCities(citiesWithLocationIds);

  // Remove duplicates that could come from the SearchPredictionStore
  const UNIQUENESS_ATTRIBUTE = 'description';
  const uniqProvinces = uniqBy(province, UNIQUENESS_ATTRIBUTE);
  const uniqPostalCodes = uniqBy(postalCode, UNIQUENESS_ATTRIBUTE);
  let finalPredictions: SearchPrediction[] = [];

  finalPredictions = [
    ...listings,
    ...uniqCities,
    ...uniqPostalCodes,
    ...uniqProvinces,
  ];

  return finalPredictions;
};


/**
  places.js:65
  As of March 1st, 2025, google.maps.places.AutocompleteService is not available to new customers.
  Please use google.maps.places.AutocompleteSuggestion instead.
  At this time, google.maps.places.AutocompleteService is not scheduled to be discontinued, but google.maps.places.AutocompleteSuggestion
  is recommended over google.maps.places.AutocompleteService. While google.maps.places.AutocompleteService will continue to receive bug fixes for any major regressions,
  existing bugs in google.maps.places.AutocompleteService will not be addressed. At least 12 months notice will be given before support is discontinued.
  Please see https://developers.google.com/maps/legacy for additional details and https://developers.google.com/maps/documentation/javascript/places-migration-overview
  for the migration guide.
*/
